import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslate } from 'react-polyglot';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import styles from './SchemaFieldCells.module.scss';
import { useAuth } from '../../../Auth';
import { useAxios } from '../../../AxiosHandler';

// Redigerbare celler for skjemaradene på Parringer og Avlshundsøknader. Alle
// lagrer via POST /api/schema/updateField, som bare godtar feltene backend har
// hvitelistet.

// Lagrer ett felt på et skjema og løses med verdien serveren faktisk lagret —
// for avhukinger er det serverens tidspunkt. Tilgangssjekken svarer 200 med
// tom liste når brukeren mangler skrivetilgang, så bare en faktisk oppdatert
// rad teller som lagret.
export function useSaveSchemaField() {
    const t = useTranslate();
    const auth = useAuth();
    const axios = useAxios().axios;

    return (row: any, field: string, value: any): Promise<any> => {
        return axios.single({
            method: 'post',
            url: "/api/schema/updateField/" + row.ID + "/" + row.BREED_ID,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" },
            data: {
                fielddata: {
                    field: field,
                    value: value,
                },
            }
        }).then((res: any) => {
            if (!(res.data?.rowsAffected > 0)) throw new Error("not saved");
            // Raden ligger i tabellens hurtigbuffer. Oppdater den, så verdien
            // står riktig når man bytter fane og kommer tilbake.
            row[field] = res.data.value;
            return res.data.value;
        }).catch((err: any) => {
            toast.error(t("general.table.savefailed"));
            throw err;
        });
    };
}

// Brukerne innlogget bruker kan velge som Kontrollør / Ansvarlig på valgt rase.
// Backend avgrenser listen til rådene brukeren selv er med i.
export function useBreedUsers(): any[] {
    const auth = useAuth();
    const axios = useAxios().axios;

    const { data = [] } = useQuery<any[]>({
        queryKey: ['breedUsers', auth.selectedBreed],
        queryFn: async () => {
            const res = await axios.single({
                method: 'get',
                url: "/api/users/forBreed/" + auth.selectedBreed,
                responseType: 'json',
                headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" },
            });
            return Array.isArray(res.data) ? res.data : [];
        },
        enabled: auth.selectedBreed != undefined,
    });

    return data;
}

// Navnet slik det vises i nedtrekk og filtervalg.
export function userDisplayName(user: any) {
    return [user.FIRSTNAME, user.LASTNAME].filter(Boolean).join(" ");
}

// Klikk i en celle skal ikke åpne skjemaet — raden navigerer ved klikk.
function stopRowClick(e: React.MouseEvent) {
    e.stopPropagation();
}

// Avhuking for ett steg i prosessen. Lagret verdi er tidspunktet steget ble
// huket av (satt av serveren), eller null.
export function StepCell(props: { row: any, field: string, label: string }) {
    const { row, field, label } = props;
    const save = useSaveSchemaField();

    const stored = row[field] ?? null;
    const [doneAt, setDoneAt] = useState<string | null>(stored);
    const [saving, setSaving] = useState(false);

    // Ny verdi fra serveren for samme rad, f.eks. etter en bakgrunnshenting.
    useEffect(() => setDoneAt(stored), [stored]);

    return (
        <span className={styles.stepcell} onClick={stopRowClick}>
            <input
                type="checkbox"
                aria-label={label}
                checked={doneAt != null}
                disabled={saving}
                onChange={(e) => {
                    const previous = doneAt;
                    const checked = e.target.checked;
                    setDoneAt(checked ? new Date().toISOString() : null);
                    setSaving(true);
                    save(row, field, checked)
                        .then((value: any) => setDoneAt(value ?? null))
                        .catch(() => setDoneAt(previous))
                        .finally(() => setSaving(false));
                }}
            />
            {doneAt != null &&
                <span className={styles.stepdate} title={new Date(doneAt).toLocaleString("no-NO")}>
                    {new Date(doneAt).toLocaleDateString("no-NO")}
                </span>
            }
        </span>
    );
}

// Samme grense som kolonnen i DE_SCHEMAS (sql/annen-info-lengde.sql).
const TEXT_MAX_LENGTH = 1000;

// Fritekstfelt («Annen info»). I tabellen vises teksten over inntil to linjer
// og avsluttes med «…»; hele teksten står i verktøytipset. Klikk eller Tab gjør
// cellen om til et tekstfelt som viser alt og vokser med innholdet, og den
// faller tilbake til visningen når feltet forlates. Lagres ved blur.
export function TextCell(props: { row: any, field: string, label: string }) {
    const { row, field, label } = props;
    const save = useSaveSchemaField();
    const ref = useRef<HTMLTextAreaElement>(null);

    const stored = row[field] ?? "";
    const [text, setText] = useState<string>(stored);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);

    // Ny verdi fra serveren — men ikke midt i skrivingen.
    useEffect(() => {
        if (!editing) setText(stored);
    }, [stored]);

    // Høyden følger innholdet mens man skriver: nullstill først, så krymper
    // feltet også når tekst slettes.
    useLayoutEffect(() => {
        const element = ref.current;
        if (!editing || element == null) return;
        element.style.height = "auto";
        element.style.height = element.scrollHeight + "px";
    }, [text, editing]);

    // Rett inn i redigering med markøren sist, som om man klikket i et felt.
    useEffect(() => {
        const element = ref.current;
        if (!editing || element == null) return;
        element.focus();
        element.setSelectionRange(element.value.length, element.value.length);
    }, [editing]);

    return (
        // Beholderen står fast når visning og redigering bytter plass, så
        // klikket aldri når raden (som åpner skjemaet).
        <div onClick={stopRowClick}>
            {editing ?
                <textarea
                    ref={ref}
                    className={`${styles.textcell} ${styles.textedit}`}
                    aria-label={label}
                    rows={1}
                    maxLength={TEXT_MAX_LENGTH}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onBlur={() => {
                        setEditing(false);
                        if (text == stored) return;
                        const previous = stored;
                        setSaving(true);
                        save(row, field, text)
                            .catch(() => setText(previous))
                            .finally(() => setSaving(false));
                    }}
                />
            :
                <div
                    className={`${styles.textcell} ${styles.textpreview}`}
                    role="button"
                    aria-label={label}
                    tabIndex={saving ? -1 : 0}
                    title={text || undefined}
                    onFocus={() => { if (!saving) setEditing(true); }}
                    onClick={() => { if (!saving) setEditing(true); }}
                >
                    {text}
                </div>
            }
        </div>
    );
}

// Nedtrekk for Kontrollør / Ansvarlig. Lagrer bruker-ID. Skjemaer fra før
// nedtrekket kom har bare fritekst i legacyField; den vises til noen velger en
// bruker, så ingen informasjon forsvinner.
export function UserSelectCell(props: { row: any, field: string, legacyField: string, users: any[] }) {
    const { row, field, legacyField, users } = props;
    const t = useTranslate();
    const save = useSaveSchemaField();

    const stored = row[field] != null ? String(row[field]) : "";
    const [userId, setUserId] = useState(stored);
    const [saving, setSaving] = useState(false);

    useEffect(() => setUserId(stored), [stored]);

    const legacyText = row[legacyField];
    const isKnownUser = users.some((user: any) => String(user.ID) == userId);

    return (
        <select
            className={styles.select}
            value={userId}
            disabled={saving}
            onClick={stopRowClick}
            onChange={(e) => {
                const previous = userId;
                const next = e.target.value;
                setUserId(next);
                setSaving(true);
                save(row, field, next == "" ? null : Number(next))
                    .catch(() => setUserId(previous))
                    .finally(() => setSaving(false));
            }}
        >
            <option value="">
                {legacyText ? legacyText + " " + t("schemafieldcells.legacytext") : t("schemafieldcells.nouser")}
            </option>
            {users.map((user: any) => (
                <option key={user.ID} value={String(user.ID)}>
                    {userDisplayName(user)}
                </option>
            ))}
            {/* Valgt bruker som innlogget bruker ikke kan se (annet råd, sysadmin),
                eller listen er ikke lastet ennå. Navnet skal ikke lekke. */}
            {userId != "" && !isKnownUser &&
                <option value={userId}>{t("schemafieldcells.unknownuser")}</option>
            }
        </select>
    );
}
